<template>
  <section v-if="featureEnabled" class="jfl" data-testid="job-families-list">
    <header class="jfl-header">
      <div class="jfl-header__title-row">
        <span class="jfl-header__icon" aria-hidden="true">
          <img
            :src="layersIconSrc"
            alt=""
            width="16"
            height="16"
          />
        </span>
        <h2 class="jfl-header__title">{{ headerTitle }}</h2>
      </div>
      <p v-if="headerSubtitle" class="jfl-header__subtitle">{{ headerSubtitle }}</p>
    </header>

    <div v-if="!isAccountEmpty" class="jfl-toolbar">
      <SearchBar
        data-testid="job-families-search"
        class="jfl-toolbar__search"
        :label="moreFiltersLabel"
        ghost-text="Search Job Family by code or name"
        :length="appliedFilterChips.length"
        :add="true"
        :filter-btn="true"
        :filter-btn-fit-content="true"
        :btn-text="'Add Job Family'"
        action-button-icon="plusicon.svg"
        :chip-text="appliedFilterChips"
        :value-search="filters.search || ''"
        :counter-section="false"
        @onSearch="onSearch"
        @onClick="onToolbarClick"
        @removeFilter="onRemoveFilter"
      />
    </div>

    <div class="jfl-body">
      <!-- Loading skeleton for the table body -->
      <div v-if="loading" class="jfl-loading" role="status" aria-live="polite">
        <span class="jfl-visually-hidden">Loading job families</span>
        <div v-for="n in 3" :key="n" class="jfl-skeleton-row" aria-hidden="true">
          <span class="jfl-skeleton-bar jfl-skeleton-bar--wide" />
          <span class="jfl-skeleton-bar jfl-skeleton-bar--count" />
          <span class="jfl-skeleton-bar jfl-skeleton-bar--count" />
          <span class="jfl-skeleton-bar jfl-skeleton-bar--short" />
          <span class="jfl-skeleton-bar jfl-skeleton-bar--icon" />
          <span class="jfl-skeleton-bar jfl-skeleton-bar--icon" />
        </div>
      </div>

      <table v-else-if="rows.length" class="jfl-table" :aria-busy="loading">
        <caption class="jfl-visually-hidden">
          Job families
        </caption>
        <colgroup>
          <col class="jfl-col-name" />
          <col class="jfl-col-levels" />
          <col class="jfl-col-positions" />
          <col class="jfl-col-status" />
          <col class="jfl-col-icon" />
          <col class="jfl-col-icon" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col" class="jfl-th">Name</th>
            <th scope="col" class="jfl-th">Levels</th>
            <th scope="col" class="jfl-th"># Positions</th>
            <th scope="col" class="jfl-th">Status</th>
            <th scope="col" class="jfl-th">
              <span class="jfl-visually-hidden">Chatter</span>
            </th>
            <th scope="col" class="jfl-th">
              <span class="jfl-visually-hidden">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in rows"
            :key="row.id"
            class="jfl-row"
            :class="rowClasses(row, index)"
          >
            <td class="jfl-cell jfl-cell--name">
              <button
                type="button"
                class="jfl-name-btn"
                :aria-label="`Open ${row.name}`"
                data-testid="job-family-name"
                @click="onRowClick(row)"
              >
                <OrgObjectCard
                  :name="row.name"
                  :codeValue="row.code"
                  :code="Boolean(row.code)"
                  :icon="true"
                  :type="'jobfamily'"
                  :state="row.cardState"
                  :allow_hover="false"
                  :showDisabledHover="false"
                  :showTooltip="false"
                  :id="row.id"
                  data-testid="job-family-card"
                />
              </button>
            </td>

            <td class="jfl-cell jfl-cell--levels">
              <span class="jfl-levels" data-testid="levels-count">
                <span class="jfl-levels__icon" aria-hidden="true">
                  <img :src="arrowsUpIconSrc" alt="" width="24" height="24" />
                </span>
                <span class="jfl-levels__count">{{ row.levelCount }}</span>
                <span class="jfl-levels__label">Levels</span>
              </span>
            </td>

            <td class="jfl-cell jfl-cell--positions">
              <AttachedToPositionsCounterButton
                :count="row.positionCount"
                :disabled="row.disabled"
                data-testid="positions-count"
              />
            </td>

            <td class="jfl-cell jfl-cell--status">
              <OrgObjectStatusBadge
                :status="row.badgeStatus"
                :neutral="row.badgeNeutral"
                data-testid="family-status-badge"
              />
            </td>

            <td class="jfl-cell jfl-cell--icon">
              <button
                type="button"
                class="jfl-icon-btn"
                :aria-label="`Open chatter for ${row.name}`"
                data-testid="row-chatter"
                @click="onRowChatter(row)"
              >
                <ChatterIcon :unread="row.unread" />
              </button>
            </td>

            <td class="jfl-cell jfl-cell--icon" @click.stop>
              <JobFamilyLineMenu
                :family="row"
                :status="row.status"
                :position-count="row.positionCount"
                @action="onLineMenuAction(row, $event)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <StandarPageWithListNoData
        v-else-if="isAccountEmpty"
        :stack-margin-top="32"
        class="jfl-empty-account"
        data-testid="job-families-empty-account"
      >
        <template #content>
          <div class="jfl-empty-account__content" role="status">
            <p class="jfl-empty-account__title">No job families yet</p>
            <p class="jfl-empty-account__description">
              Group positions by discipline by adding a job family.
            </p>
            <Button
              :general="true"
              plusIcon="add-icon.svg"
              label="Add your first job family"
              data-testid="job-families-empty-add"
              @onClick="emit('add-family')"
            />
          </div>
        </template>
      </StandarPageWithListNoData>

      <div v-else class="jfl-empty" role="status">
        <p class="jfl-empty__title">No job families found</p>
        <p class="jfl-empty__subtitle">Try changing the filters or clearing your search.</p>
      </div>
    </div>

    <ExPagination
      v-if="pagination.pageCount > 1"
      class="jfl-pagination"
      :pagination="paginationForEx"
      @onRequest="onPaginationRequest"
    />
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";

import SearchBar from "@components/SearchBar/SearchBar.vue";
import Button from "@components/buttons/Button.vue";
import StandarPageWithListNoData from "@components/common/StandarPageWithListNoData.vue";
import OrgObjectCard from "@components/OrgObjectCard/OrgObjectCard.vue";
import OrgObjectStatusBadge from "@components/organizations/statusBadge/OrgObjectStatusBadge.vue";
import AttachedToPositionsCounterButton from "@components/AttachedToPositionsCounterButton/AttachedToPositionsCounterButton.vue";
import ChatterIcon from "@components/ChatterIcon/ChatterIcon.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import JobFamilyLineMenu from "@components/JobFamilyLineMenu/JobFamilyLineMenu.vue";
import { LD_FLAG_IDEA_MDP_581 } from "@components/constants/launchDarklyFlagKeys";
import { buildRowView } from "./jobFamiliesListRow.js";

const layersIconSrc = require("@components/assets/job-family/layers-three-01.svg");
const arrowsUpIconSrc = require("@components/assets/job-family/arrows-up.svg");

/**
 * ER-11386 — JobFamiliesList (Organization > Job Families table view).
 *
 * Presentational composition of existing shared components (filters bar/SearchBar,
 * OrgObjectCard type="jobfamily" with the layers-three glyph, OrgObjectStatusBadge,
 * AttachedToPositionsCounterButton for # Positions, ChatterIcon, ExPagination)
 * for the Organization > Job Families screen. OrganizationJobFamiliesTab owns
 * data fetching against GET /job-family/, filtering/pagination state and the
 * filter modal / add dialog; this component only re-emits intent through `filter-change`, `more-filters`,
 * `remove-filter`, `add-family`, `row-click`, `row-chatter`, `row-actions`
 * and `page-change`.
 *
 * Shipped behind the linked idea flag (idea-MDP-581, "Job Family — Discipline &
 * Career-Ladder Grouping for Positions"). Kill-switch semantics: renders by
 * default; set the flag to false to hide the new table until a screen wires it.
 */
const props = defineProps({
  jobFamilies: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  filters: {
    type: Object,
    default: () => ({ status: "", search: "" }),
  },
  pagination: {
    type: Object,
    default: () => ({ page: 1, pageCount: 1 }),
  },
  appliedFilterChips: {
    type: Array,
    default: () => [],
  },
  headerTitle: {
    type: String,
    default: "Job Families",
  },
  headerSubtitle: {
    type: String,
    default: "Discipline-based Position groupings, each with a career-ladder of Levels.",
  },
  isAccountEmpty: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "add-family",
  "filter-change",
  "more-filters",
  "remove-filter",
  "row-click",
  "row-chatter",
  "row-actions",
  "row-action",
  "page-change",
]);

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_581, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
});

const rows = computed(() => props.jobFamilies.map((family) => buildRowView(family)));

const moreFiltersLabel = computed(() =>
  props.appliedFilterChips.length > 0 ? "More filters" : "Filters"
);

const paginationForEx = computed(() => {
  const pageCount = Number(props.pagination.pageCount) || 1;
  const requestedPage = Number(props.pagination.page) || 1;
  return {
    page: Math.min(Math.max(requestedPage, 1), pageCount),
    total: pageCount,
    perPage: 1,
  };
});

function rowClasses(row, index) {
  return {
    "jfl-row--alt": index % 2 === 1,
    "jfl-row--disabled": row.disabled,
  };
}

function onSearch(searchText) {
  emit("filter-change", { ...props.filters, search: searchText });
}

function onToolbarClick(payload) {
  if (payload === "add") {
    emit("add-family");
    return;
  }
  emit("more-filters");
}

function onRemoveFilter(chipLabel) {
  emit("remove-filter", chipLabel);
}

function onRowClick(row) {
  emit("row-click", row.id);
}

function onRowChatter(row) {
  emit("row-chatter", row.id);
}

function onLineMenuAction(row, payload) {
  const action = payload && payload.type;
  if (!action) return;
  emit("row-action", { id: row.id, action });
  emit("row-actions", row.id);
}

function onPaginationRequest({ page }) {
  emit("page-change", page);
}
</script>

<style scoped>
.jfl {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  overflow: hidden;
}

.jfl-header {
  padding: 24px 24px 20px;
}

.jfl-header__title-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.jfl-header__icon {
  display: flex;
  width: 16px;
  height: 16px;
  overflow: hidden;
  flex-shrink: 0;
}

.jfl-header__icon img {
  display: block;
  width: 16px;
  height: 16px;
}

.jfl-header__title {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.jfl-header__subtitle {
  margin: 4px 0 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.jfl-toolbar {
  display: flex;
  align-items: center;
  padding: 20px;
}

.jfl-toolbar__search {
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.jfl-body {
  width: 100%;
  overflow-x: auto;
}

.jfl-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.jfl-col-name {
  width: auto;
}

.jfl-col-levels {
  width: 160px;
}

.jfl-col-positions {
  width: 140px;
}

.jfl-col-status {
  width: 120px;
}

.jfl-col-icon {
  width: 72px;
}

.jfl-table thead th {
  height: 44px;
  padding: 12px 24px;
  border-bottom: 1px solid #eaecf0;
  background: #f9fafb;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}

.jfl-table tbody td {
  height: 64px;
  padding: 0 16px;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle;
}

.jfl-row--alt td {
  background: #fcfcfd;
}

.jfl-cell--name {
  text-align: left;
}

.jfl-cell--icon {
  text-align: center;
}

.jfl-name-btn {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  height: 100%;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font: inherit;
  color: inherit;
  text-align: left;
}

.jfl-name-btn:hover ::v-deep .org-object-card-compass {
  background: #f2edff;
  border-radius: 4px;
}

.jfl-name-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.jfl-cell--name ::v-deep .org-object-card-root {
  display: flex;
  align-items: center;
  max-width: 100%;
}

.jfl-cell--name ::v-deep .org-object-card-compass {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  max-width: 100%;
  padding: 4px;
  min-height: 24px;
}

.jfl-cell--name ::v-deep .org-object-icon-container,
.jfl-cell--name ::v-deep .object-card-code,
.jfl-cell--name ::v-deep .object-card-name {
  display: inline-flex;
  align-items: center;
  line-height: 24px;
}

.jfl-cell--name ::v-deep .code-text,
.jfl-cell--name ::v-deep .name-text {
  line-height: 24px;
}

.jfl-levels {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  min-height: 24px;
}

.jfl-cell--positions ::v-deep .attached-to-positions-counter {
  display: inline-flex;
  align-items: center;
  height: 100%;
  min-height: 24px;
}

.jfl-levels__icon {
  display: flex;
  width: 24px;
  height: 24px;
  overflow: hidden;
  flex-shrink: 0;
}

.jfl-levels__icon img {
  display: block;
  width: 24px;
  height: 24px;
}

.jfl-levels__count {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #98a2b0;
  font-variant-numeric: tabular-nums;
}

.jfl-levels__label {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #101828;
}

.jfl-row--disabled .jfl-levels__label {
  color: #98a2b3;
}

.jfl-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #475467;
  cursor: pointer;
}

.jfl-icon-btn:hover {
  background: #f2edff;
}

.jfl-icon-btn:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.jfl-actions-icon {
  display: flex;
  width: 24px;
  height: 24px;
  overflow: hidden;
}

.jfl-actions-icon img {
  display: block;
  width: 24px;
  height: 24px;
}

.jfl-loading {
  padding: 8px 24px;
}

.jfl-skeleton-row {
  display: grid;
  grid-template-columns: 2.2fr 0.9fr 0.9fr 0.7fr 0.4fr 0.4fr;
  gap: 16px;
  align-items: center;
  padding: 16px 0;
  border-bottom: 1px solid #eaecf0;
}

.jfl-skeleton-row:last-child {
  border-bottom: none;
}

.jfl-skeleton-bar {
  display: block;
  height: 14px;
  border-radius: 9999px;
  background: #f2f4f7;
}

.jfl-skeleton-bar--wide {
  width: 70%;
}

.jfl-skeleton-bar--count {
  width: 45%;
}

.jfl-skeleton-bar--short {
  width: 45%;
}

.jfl-skeleton-bar--icon {
  width: 24px;
  height: 24px;
  justify-self: center;
}

.jfl-empty {
  padding: 40px 24px;
  text-align: center;
}

.jfl-empty__title {
  margin: 0 0 4px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.jfl-empty__subtitle {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.jfl-empty-account__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 32px;
  text-align: center;
}

.jfl-empty-account__title {
  margin: 0 0 8px;
  font-family: Inter, sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.jfl-empty-account__description {
  margin: 0 0 24px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.jfl-pagination {
  border-top: 1px solid #eaecf0;
}

.jfl-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
